import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import participantsStyles from '~/styles/bookingFlow/participants.module.css';
import useTranslation from '~/hooks/useTranslation';
import FormGroup from '~/components/Form/FormGroup';
import Radio from '~/components/Form/Radio';
import TextField from '~/components/Form/TextField';
import useGenderOptions from '~/hooks/bookingFlow/useGenderOptions';
import DateField from '~/components/Form/DateField';
import formatDate from '~/helpers/formatDate';
import type { InferredBookingFlowOptions } from '~/types/options';
import { useOptions } from '~/hooks/useOptions';
import type { PackageInformationRequest } from '~/types/api';
import InformationCard from './InformationCard';
import ParticipantNotes from './ParticipantNotes';

type Props = {
  registerFieldPath: string;
  className?: string;
};

export default function Participants({
  registerFieldPath,
  className,
}: Props) {
  const translate = useTranslation();
  const genderOptions = useGenderOptions();
  const options = useOptions() as InferredBookingFlowOptions;

  const participants = useWatch({ name: registerFieldPath }) as PackageInformationRequest['participants'];

  const mandatoryFields = useMemo(() => {
    return options.mandatoryFields?.split(',').map((field) => field.trim());
  }, [options.mandatoryFields]);

  return (
    <InformationCard
      className={className}
      title={translate('bookingFlow.information.participants_title')}
      description={translate('bookingFlow.information.participants_description')}
    >
      <div className={participantsStyles.participants}>
        {participants.map((participant, index) => {
          // The main booker has its own form, we only show a summary
          if (index === 0) {
            const name = participant.first_name && participant.last_name
              ? `${participant.first_name} ${participant.last_name}`
              : translate('bookingFlow.information.main_booker_empty');

            const birthDate = participant.birth_date
              ? formatDate(participant.birth_date, 'd-m-Y')
              : translate('bookingFlow.information.main_booker_empty');

            return (
              <div key={participant.index} className={participantsStyles.participantsRow}>
                <h4 className={participantsStyles.participantsTitle}>
                  {translate('bookingFlow.information.main_booker_title')}
                </h4>

                <p className={participantsStyles.participantsMainBookerName}>
                  {`${translate('form.name')}: ${name}`}
                  <br />
                  {`${translate('form.birth_date')}: ${birthDate}`}
                </p>
              </div>
            );
          }

          return (
            <div
              key={participant.index}
              className={participantsStyles.participantsRow}
            >
              <h4 className={participantsStyles.participantsTitle}>
                {translate('bookingFlow.information.fellow_participant', { index: index + 1 })}
              </h4>

              <FormGroup>
                <Radio
                  label={translate('form.gender')}
                  items={genderOptions}
                  registerField={`${registerFieldPath}.${index}.gender`}
                  required
                />
              </FormGroup>

              <div className={participantsStyles.participantsNameRow}>
                <FormGroup>
                  <TextField
                    label={translate('form.first_name')}
                    registerField={`${registerFieldPath}.${index}.first_name`}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <TextField
                    label={translate('form.last_name')}
                    registerField={`${registerFieldPath}.${index}.last_name`}
                    required
                  />
                </FormGroup>
              </div>

              {options.passportFields && (
                <FormGroup>
                  <TextField
                    label={translate('form.passport_number')}
                    fieldType="text"
                    registerField={`${registerFieldPath}.${index}.passport_number`}
                    min={1}
                    max={200}
                    required={mandatoryFields?.includes('passport_number') ?? false}
                  />
                </FormGroup>
              )}

              <FormGroup>
                <DateField
                  label={translate('form.birth_date')}
                  registerField={`${registerFieldPath}.${index}.birth_date`}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </FormGroup>

              <ParticipantNotes
                registerFieldPath={`${registerFieldPath}.${index}`}
              />
            </div>
          );
        })}
      </div>
    </InformationCard>
  );
}
